import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home } from 'lucide-react';
import { useBatmanAuth } from '../context/BatmanAuthContext';
import { LogoIcon } from '../components/Logo';

const Signup = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { signup } = useBatmanAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    const result = await signup(username, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Signup failed');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#111111] flex relative">
      <Link
        to="/"
        className="absolute top-6 right-6 z-10 inline-flex items-center gap-2 px-4 py-2 text-white/50 hover:text-white border border-white/10 hover:border-white/20 transition-colors"
      >
        <Home className="h-4 w-4" />
        <span className="text-sm">Home</span>
      </Link>

      <div className="flex-1 flex flex-col justify-center px-6 md:px-12 lg:px-20 py-12">
        <div className="max-w-md">
          <div className="flex items-center gap-2 mb-12">
            <LogoIcon className="h-8 w-8" />
          </div>

          <p className="text-white/50 mb-4">Get started with Batman app today</p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-10 border border-white/20 bg-white/5 text-white text-xs font-mono tracking-wide">
            <span>✦</span>
            <span>SECURE ACCOUNT CREATION</span>
          </div>

          {error && (
            <div className="mb-6 p-4 border border-red-500/30 bg-red-500/10">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-white/40 text-xs font-mono tracking-widest mb-3">USERNAME</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                disabled={isLoading}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Choose a username"
              />
            </div>

            <div>
              <label className="block text-white/40 text-xs font-mono tracking-widest mb-3">PASSWORD</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                disabled={isLoading}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Create a password"
              />
            </div>

            <div>
              <label className="block text-white/40 text-xs font-mono tracking-widest mb-3">CONFIRM PASSWORD</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="Confirm your password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="mt-8 text-white/50 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-white hover:text-white/80 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute -inset-y-[25%] -right-24 flex w-full flex-col blur md:-right-6"
          style={{
            maskImage: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgb(255, 255, 255))',
            opacity: 1,
          }}
        >
          <div
            className="grow"
            style={{
              background:
                'conic-gradient(from 180deg at 99.78% 35% in lab, rgb(255, 255, 255) 18deg, rgb(255, 208, 134) 36deg, rgba(17, 17, 17, 0) 90deg, rgba(17, 17, 17, 0) 342deg, rgb(255, 255, 255) 360deg)',
            }}
          />
          <div
            className="grow"
            style={{
              background:
                'conic-gradient(from 0deg at 99.78% 65% in lab, rgb(255, 255, 255) 0deg, rgba(17, 17, 17, 0) 18deg, rgba(17, 17, 17, 0) 270deg, rgb(255, 208, 134) 324deg, rgb(255, 255, 255) 342deg)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Signup;
